/**
 * The dashboard widget registry - what a widget IS, and every widget there is.
 *
 * A widget is a section component plus the four things a grid needs in order
 * to place it, gate it and offer it in a gallery: how wide and tall it wants to
 * be, what it is called, and whether this user should see it at all. The
 * section components themselves are unchanged and own no layout - they take
 * props and render a card. Everything about WHERE they go lives here and in
 * `widgetLayout`.
 *
 * TWO KINDS OF GATE, and the distinction matters:
 *
 *   `requires` is the WIDGET-level gate - a licence section the whole card
 *   depends on, or data without which the card is an empty box. It runs before
 *   layout, so a gated-out widget is absent from the flow entirely and the ones
 *   after it move up. This is where `HomePage`'s `processingItems.length > 0`
 *   condition and the section components' own `hasSectionAccess(…) return null`
 *   early-returns have been consolidated.
 *
 *   Per-ITEM gating stays inside the component, because it is not a layout
 *   question: QuickMetrics still drops the tiles a user has no licence for,
 *   ToolsGrid still drops individual tools. Where a widget's existence depends
 *   on that same per-item filter, the predicate is exported from the component
 *   and used by both (`accessibleTools`, `accessibleProcessingItems`) so the
 *   two can never disagree.
 *
 * `render` takes the whole `/dashboard/summary` slice and pulls out what it
 * needs. There is still exactly one request behind the page; widgets remain
 * pure renderers of it.
 */

import React from 'react';
import {
  PiBellLight,
  PiBellRingingLight,
  PiCalendarLight,
  PiChartLineUpLight,
  PiClockCounterClockwiseLight,
  PiGaugeLight,
  PiHandWavingLight,
  PiSpinnerGapLight,
  PiSquaresFourLight,
  PiTrendUpLight,
  PiWarningLight,
  PiWrenchLight,
} from 'react-icons/pi';
import { DashboardState } from '../../../store/reducer/DashboardReducer';
import { WidgetHeight, WidgetSpan } from '../widgetLayout';

import GreetingBar from '../sections/GreetingBar';
import NeedsAttention from '../sections/NeedsAttention';
import QuickMetrics from '../sections/QuickMetrics';
import ProcessingItems, { accessibleProcessingItems } from '../sections/ProcessingItems';
import ToolsGrid, { accessibleTools } from '../sections/ToolsGrid';
import RecentActivityFeed from '../sections/RecentActivityFeed';
import UpcomingEvents from '../sections/UpcomingEvents';
import MetricsGrid from '../sections/MetricsGrid';
import ProductivityBar from '../sections/ProductivityBar';
import UrgentAttentionPanel from '../sections/UrgentAttentionPanel';
import FirmEconomicsSnapshot from '../sections/FirmEconomicsSnapshot';
import NotificationsWidget from '../sections/NotificationsWidget';

export type DashboardWidgetId =
  | 'greeting'
  | 'needsAttention'
  | 'quickMetrics'
  | 'processing'
  | 'tools'
  | 'activity'
  | 'upcomingEvents'
  | 'metricsGrid'
  | 'productivity'
  | 'urgentAttention'
  | 'firmEconomics'
  | 'notifications';

/** What a widget renders from: the summary slice, and whether it is in flight. */
export interface WidgetRenderProps {
  data: DashboardState;
  loading: boolean;
  /**
   * Re-run the page's one request.
   *
   * Widgets that own a chronology take this so their "refresh" means the same
   * thing the page header's does - a card with its own private idea of how to
   * reload is how two views of the same data drift apart.
   */
  onRefresh: () => void;
}

/**
 * What `requires` gets to decide with.
 *
 * The licence checks are passed in rather than read from a hook, so the whole
 * registry stays a plain value that a test can evaluate without a store.
 */
export interface WidgetGate {
  data: DashboardState;
  hasSectionAccess: (section: string) => boolean;
  hasAiFeatureAccess: (feature: string) => boolean;
}

/**
 * What a widget is FOR, and therefore which group of the Customize panel it
 * appears under.
 *
 * The gallery used to be one flat list, which was survivable at nine widgets
 * and unreadable past that - and, worse, made a catalogue of two leftovers look
 * like the whole of what a dashboard can be. Grouping states the shape of the
 * catalogue even when most of it is already placed.
 */
export type WidgetCategory = 'orientation' | 'work' | 'insight' | 'context';

/** Group order and headings, for the gallery and anything else that lists. */
export const WIDGET_CATEGORIES: Array<{ id: WidgetCategory; label: string; description: string }> = [
  {
    id: 'orientation',
    label: 'Start here',
    description: 'Where you stand the moment the page opens',
  },
  { id: 'work', label: 'Your work', description: 'The records you are actually working on' },
  { id: 'insight', label: 'Numbers', description: 'Counters, money and what the AI saved you' },
  { id: 'context', label: 'Context', description: 'What changed, what is coming, what is running' },
];

export interface WidgetDefinition {
  id: DashboardWidgetId;
  /** Translation key - the gallery and the edit-mode chrome show this. */
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Which group of the Customize catalogue this belongs under. */
  category: WidgetCategory;
  defaultSpan: WidgetSpan;
  defaultHeight: WidgetHeight;
  /** How far the user may narrow it before it stops being readable. */
  minSpan?: WidgetSpan;
  /**
   * How far a widget may be WIDENED by the flow.
   *
   * `flowRows` hands a short row's leftover columns to its last widget so the
   * grid never renders a hole - which is right for a list, and wrong for a card
   * designed as one of a pair: when Recent Activity's row-mate was gated away,
   * Activity silently became a full-width letterbox. A `maxSpan` says "I am
   * half a row" and the flow leaves the gap alone.
   *
   * It does NOT limit what the USER may drag to - someone who widens a card
   * deliberately gets what they asked for.
   */
  maxSpan?: WidgetSpan;
  /** The widget-level gate. Absent means always available. */
  requires?: (gate: WidgetGate) => boolean;
  /** Preserved from the hand-written rows so the product tour still lands. */
  tourId?: string;
  render: (props: WidgetRenderProps) => React.ReactNode;
}

export const WIDGETS: Record<DashboardWidgetId, WidgetDefinition> = {
  /*
    THE TOP ROW IS THREE CARDS NOW, not two.

    The greeting used to be half the row and carried the day's one actionable
    line inside it ("3 tasks are overdue") as plain text. It is a quarter of a
    row of pure welcome; the actionable half became `needsAttention`, which is
    a card of clickable rows on the other side of "At a glance". Read across:
    who you are · where you stand · what wants you.
  */
  greeting: {
    id: 'greeting',
    label: 'Greeting',
    description: 'Time-of-day welcome and today’s date',
    icon: PiHandWavingLight,
    category: 'orientation',
    defaultSpan: 1,
    // Compact: a two-line welcome needs no more (UI feedback D-4). The 'sm'
    // floor (11rem) left it a tall, mostly-empty card.
    defaultHeight: 'xs',
    minSpan: 1,
    tourId: 'dashboard-greeting',
    render: () => <GreetingBar />,
  },

  needsAttention: {
    id: 'needsAttention',
    label: 'Needs you',
    description: 'Overdue and due-today work, the next meeting, anything still processing',
    icon: PiBellRingingLight,
    category: 'orientation',
    defaultSpan: 1,
    defaultHeight: 'sm',
    minSpan: 1,
    render: ({ data }) => (
      <NeedsAttention
        tasks={data.urgentTasks}
        events={data.upcomingEvents}
        processing={data.processingItems}
      />
    ),
  },

  quickMetrics: {
    id: 'quickMetrics',
    label: 'At a glance',
    description: 'The counters you choose - tasks, matters, projects, reviews, files',
    icon: PiGaugeLight,
    category: 'orientation',
    defaultSpan: 2,
    defaultHeight: 'sm',
    minSpan: 2,
    tourId: 'dashboard-metrics',
    // The tiles gate themselves one by one; the CARD only disappears when
    // there is not a single tile left to show.
    requires: ({ hasSectionAccess }) =>
      ['tasks', 'matters', 'projects', 'review'].some(hasSectionAccess),
    render: ({ data }) => <QuickMetrics overview={data.overview} />,
  },

  processing: {
    id: 'processing',
    label: 'Currently Processing',
    description: 'Matters and reviews the AI is still working through',
    icon: PiSpinnerGapLight,
    category: 'context',
    defaultSpan: 4,
    defaultHeight: 'sm',
    minSpan: 2,
    // Was `{processingItems.length > 0 && …}` in HomePage plus the component's
    // own per-item licence filter - one predicate now, shared with the render.
    requires: ({ data, hasSectionAccess }) =>
      accessibleProcessingItems(data.processingItems, hasSectionAccess).length > 0,
    render: ({ data }) => <ProcessingItems items={data.processingItems} />,
  },

  tools: {
    id: 'tools',
    label: 'Tools',
    description: 'Shortcuts into research, review, tables and templates',
    icon: PiWrenchLight,
    category: 'work',
    // Half-width by default, not a full-row banner of shortcuts (UI feedback
    // D-8). minSpan 1 lets users narrow it further from the dashboard controls.
    defaultSpan: 2,
    defaultHeight: 'sm',
    minSpan: 1,
    tourId: 'dashboard-tools',
    requires: ({ hasSectionAccess }) => accessibleTools(hasSectionAccess).length > 0,
    render: () => <ToolsGrid />,
  },

  /*
    `md`, not `sm`, here and on `upcomingEvents`: a chronology with three
    entries in a short box reads as a broken widget. At `md` a short feed sits
    calmly in a 28rem card with no scrollbar at all, and a long one scrolls
    inside it - see `HEIGHT_CLASS` for why a preset is a floor AND a ceiling.
  */
  activity: {
    id: 'activity',
    label: 'Recent Activity',
    description: 'What you and your collaborators changed lately',
    icon: PiClockCounterClockwiseLight,
    category: 'context',
    defaultSpan: 2,
    defaultHeight: 'md',
    // minSpan 1 so it can be narrowed below half from the dashboard controls
    // (UI feedback D-9).
    minSpan: 1,
    tourId: 'dashboard-activity',
    render: ({ data, loading, onRefresh }) => (
      <RecentActivityFeed
        activities={data.recentActivity}
        loading={loading && data.recentActivity.length === 0}
        onRefresh={onRefresh}
      />
    ),
  },

  upcomingEvents: {
    id: 'upcomingEvents',
    label: 'Upcoming Events',
    description: 'Hearings, deadlines and meetings from your calendar',
    icon: PiCalendarLight,
    category: 'context',
    defaultSpan: 2,
    defaultHeight: 'md',
    minSpan: 2,
    requires: ({ data, hasSectionAccess }) =>
      hasSectionAccess('calendar') && data.upcomingEvents.length > 0,
    render: ({ data }) => <UpcomingEvents events={data.upcomingEvents} />,
  },

  /*
    Below: built, gated and wired, but not in the default arrangement. They are
    registered so the "add widget" gallery has something to offer - a widget
    nobody can find is the same as a widget nobody built.
  */

  metricsGrid: {
    id: 'metricsGrid',
    label: 'All Metrics',
    description: 'The full counter grid - files, tables, research, drafts',
    icon: PiSquaresFourLight,
    category: 'insight',
    defaultSpan: 4,
    defaultHeight: 'sm',
    minSpan: 2,
    // Its tiles gate themselves per section (and per AI feature, where a
    // metric declares one); the card goes when none of them survive.
    requires: ({ hasSectionAccess }) =>
      ['projects', 'matters', 'tasks', 'review', 'clients', 'tables', 'files']
        .some(hasSectionAccess),
    render: ({ data }) => <MetricsGrid overview={data.overview} />,
  },

  productivity: {
    id: 'productivity',
    label: 'Productivity',
    description: 'Hours saved and what saved them, last 30 days',
    icon: PiTrendUpLight,
    category: 'insight',
    // Half a row, beside Recent Activity - it shipped full width, which made
    // three numbers into a letterbox.
    defaultSpan: 2,
    defaultHeight: 'md',
    minSpan: 2,
    maxSpan: 2,
    requires: ({ data }) => {
      const p = data.productivity;
      return !!p && (p.hours_saved > 0 || p.tasks_completed_30d > 0);
    },
    render: ({ data }) => <ProductivityBar productivity={data.productivity} />,
  },

  urgentAttention: {
    id: 'urgentAttention',
    label: 'Your Work',
    description: 'Project deadlines, upcoming tasks and recent matters, side by side',
    icon: PiWarningLight,
    category: 'work',
    defaultSpan: 4,
    defaultHeight: 'lg',
    minSpan: 2,
    // The three standalone list widgets (Recent Matters / Upcoming Tasks / My
    // Projects) were removed as duplicates of this card's three columns, so
    // the product tour's three separate stops collapse onto this one.
    tourId: 'dashboard-work',
    /*
      THE GATE HAD TO RELAX WHEN THIS BECAME THE ONLY WORK CARD.

      It used to require that something was actually urgent - reasonable while
      it was a supplementary "needs attention" panel sitting above three lists
      that always rendered. Now that those lists are gone, that same condition
      means a brand-new account, or anyone who is simply on top of their work,
      opens a dashboard with no matters, no tasks, no projects and - worse —
      no way to create any of them, because the create actions live in this
      card's columns. Each column carries its own empty state, so an empty card
      is informative rather than broken.
    */
    requires: ({ hasSectionAccess }) =>
      ['tasks', 'matters', 'projects'].some(hasSectionAccess),
    render: ({ data }) => (
      <UrgentAttentionPanel
        urgentTasks={data.urgentTasks}
        urgentMatters={data.urgentMatters}
        urgentProjects={data.urgentProjects}
      />
    ),
  },
  firmEconomics: {
    id: 'firmEconomics',
    label: 'Firm Economics',
    description: 'Revenue, hours and billable share across the organisation',
    icon: PiChartLineUpLight,
    category: 'insight',
    defaultSpan: 4,
    defaultHeight: 'md',
    minSpan: 2,
    /*
      Two gates, both necessary. `is_org_admin` is the one that matters - firm
      economics is not a member's card, and the backend will refuse it anyway;
      offering it in the gallery to someone who would get a 403 is a worse
      failure than not offering it. The licence check is the ordinary one.
    */
    requires: ({ data, hasSectionAccess }) =>
      !!data.overview?.is_org_admin && hasSectionAccess('finance'),
    render: () => <FirmEconomicsSnapshot />,
  },

  notifications: {
    id: 'notifications',
    label: 'Notifications',
    description: 'Your unread notifications, newest first',
    icon: PiBellLight,
    category: 'context',
    defaultSpan: 2,
    defaultHeight: 'md',
    minSpan: 2,
    // No gate: the notification centre is available to every seat, and the
    // card says something useful ("nothing needs you") even when it is empty.
    render: () => <NotificationsWidget />,
  },
};

/** Every widget id, in registration order - the gallery's order too. */
export const WIDGET_IDS = Object.keys(WIDGETS) as DashboardWidgetId[];

/** Whether this user should see a widget at all. */
export const widgetAllowed = (id: DashboardWidgetId, gate: WidgetGate): boolean => {
  const definition = WIDGETS[id];
  if (!definition) return false;
  return definition.requires ? definition.requires(gate) : true;
};

export default WIDGETS;
